import z, { core, output, ZodError, ZodObject, ZodType } from "zod";
import { flattenObjectPaths, getShapeByPath, getValueByPath } from "./utils";

export class AlpineValidationContext<
    V extends { [key:string]: any },
    T extends ZodObject<Shape, Config>,
    Shape extends core.$ZodShape,
    Config extends core.$ZodObjectConfig> {
    public formState: FormState = new FormState();
    public schema: T;
    public validatableObject: V;
    public eventListeners: FieldListener[] = [];

    constructor(validationSchema: T, validatableObject: V) {
        this.schema = validationSchema;
        this.validatableObject = validatableObject;
    }

    // public setupListeners(element: HTMLElement, $nextTick: typeof AlpineComponent.prototype.$nextTick) {
    //     Array.from(element.querySelectorAll("[x-model]"))
    //         .forEach((input) => {
    //             const field = input.getAttribute("x-model");
    //             if (!field || !flattenObjectPaths(this.schema).includes(field)) return;

    //             let listener = input.addEventListener("change", () =>{
    //                 $nextTick(()=>{
    //                     this.validateOnly(field);
    //                 });
    //             });
    //             this.eventListeners.push(new FieldListener(field, listener, 'change'));

    //             listener = input.addEventListener("input", () =>{
    //                 $nextTick(()=>{
    //                     if(this.isInvalid(field)) {
    //                         this.validateOnly(field);
    //                     }
    //                 });
    //             });
    //             this.eventListeners.push(new FieldListener(field, listener, 'input'));
    //         });
    // }

    private parseZodErrors(zodError: ZodError<output<T>>) : Record<string, string[]> {
        const errors: Record<string, string[]> = {};

        //return z.flattenError(zodError).fieldErrors as Record<string, string[]>;

        zodError.issues.forEach((issue) => {
            const fieldPath = issue.path.join('.');
            if (!errors[fieldPath]) {
                errors[fieldPath] = [];
            }
            errors[fieldPath].push(issue.message);
        });

        return errors;
    }

    private parseZodSuccesses(zodError: ZodError<output<T>>) : Record<string,boolean> {
        const errors: Record<string, boolean> = {};
        flattenObjectPaths(this.schema).forEach((fieldPath) => {
            errors[fieldPath] = true;
        });

        zodError.issues.forEach((issue) => {
            const fieldPath = issue.path.join('.');
            delete errors[fieldPath];
        });
        return errors;
    }

    public validate(silent: boolean = false) : { outcome?: boolean, successes: Record<string, boolean>, errors: Record<string, string[]> } {
        const result = this.schema.safeParse(this.validatableObject);

        const outcome = result.success && !result.error;
        const successes = result.error ? this.parseZodSuccesses(result.error) : {}
        const errors = result.error ? this.parseZodErrors(result.error) : {}

        if(!silent && result.error) {
            this.formState.errors = errors;
            this.formState.successes = successes;
        }

        return {
            outcome: outcome,
            successes: successes,
            errors: errors
        };
    }

    private processZodFieldValidation(field: string) {
        const fieldSchema = getShapeByPath(this.schema, field) as ZodType ?? null;
        if (!fieldSchema) {
            console.warn(`Validation: No validation schema defined for the field: ${field}`);
            return false;
        }

        const result = fieldSchema.safeParse(getValueByPath(this.validatableObject, field));

        if (result.success) {
            delete this.formState.errors[field];
            this.formState.successes[field] = true;
            return true;
        }

        this.formState.errors[field] = [z.treeifyError(result.error).errors[0]];
        this.formState.successes[field] = false;
        return false;
    }

    public isValid(field :string) {
        return this.formState.successes[field] ?? false;
    }
    public isInvalid(field :string) {
        return Object.keys(this.formState.errors).includes(field);
    }
    public firstErrorFor(field :string) {
        if (this.formState.errors[field]) {
            return this.formState.errors[field][0] ?? null;
        }
        return null;
    }
    public getErrorsFor(field : string) {
        return this.formState.errors[field] ?? [];
    }
    public allErrors() {
        return this.formState.errors;
    }
    public hasErrors() {
        return Object.keys(this.formState.errors).length > 0;
    }
    public allSuccesses() {
        return this.formState.successes;
    }
    public reset() {
        this.formState.errors = {} as Record<string, string[]>;
        this.formState.successes = {} as Record<string, boolean>;
    }
    public validateOnly(field: string) {
        const result = this.processZodFieldValidation(field);
        return result;
    }
}

export class FormState {
    errors: Record<string, string[]> = {};
    successes: Record<string, boolean> = {};
}
export class FieldListener {
    field: string;
    listener: void;
    event: string;

    constructor(field: string, listener: void, event: string) {
        this.field = field;
        this.listener = listener;
        this.event = event;
    }
}